import { fallbackLng } from 'i18n-settings';
import type { StateStorage } from 'zustand/middleware';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import type { EditingLanguageStore } from './types';

// ------------- Constants -------------

const STORAGE_KEY = 'editingLanguage';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

// ------------- Cookie Storage Adapter -------------

const cookieStorage: StateStorage = {
  getItem: (name: string): string | null => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match === null ? null : decodeURIComponent(match[1] ?? '');
  },
  setItem: (name: string, value: string): void => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
  },
  removeItem: (name: string): void => {
    document.cookie = `${name}=; path=/; max-age=0`;
  },
};

// ------------- Store Factory -------------

export function createEditingLanguageStore() {
  return createStore<EditingLanguageStore>()(
    persist(
      (set, get) => ({
        // ------------- Initial State -------------
        editingLanguage: fallbackLng,
        editableLanguages: [],

        // ------------- Actions -------------

        setEditingLanguage: (lang: string): void => {
          const { editingLanguage: current } = get();
          if (lang === current) {
            return;
          }
          set({ editingLanguage: lang });
        },

        setEditableLanguages: (langs: string[], preferredLanguage?: string): void => {
          const { editingLanguage: current } = get();

          set({ editableLanguages: langs });

          // Validate current editing language against new editable list.
          // Priority when the current value isn't editable:
          //   preferredLanguage (e.g. the UI locale) if it's editable → first editable.
          if (langs.length > 0 && langs.includes(current) === false) {
            const nextLang = preferredLanguage !== undefined && langs.includes(preferredLanguage)
              ? preferredLanguage
              : (langs[0] ?? fallbackLng);
            set({ editingLanguage: nextLang });
          }
        },
      }),
      {
        name: STORAGE_KEY,
        storage: createJSONStorage(() => cookieStorage),
        partialize: state => ({ editingLanguage: state.editingLanguage }),
      },
    ),
  );
}

// ------------- Store Type -------------

export type EditingLanguageStoreApi = ReturnType<typeof createEditingLanguageStore>;
